import { captureException } from '@sentry/react';
import {
  useRouteError,
  isRouteErrorResponse,
  generatePath,
} from 'react-router-dom';

import {
  InternalServerError,
  LoadingError,
  NotFoundError,
  PermissionDeniedError,
} from '@platform/core/errors';

import { InternalServerErrorPage } from '../../../components/errors/internal-server-error-page';
import { LoadingErrorPage } from '../../../components/errors/loading-error-page';
import { NotFoundErrorPage } from '../../../components/errors/not-found-error-page';
import { PermissionDeniedErrorPage } from '../../../components/errors/permission-denied-error-page';

export const FeatureErrorElement = () => {
  const error = useRouteError();

  if (import.meta.env.MODE === 'development') {
    // eslint-disable-next-line no-console
    console.warn(
      'platform-client: Error was caught by FeatureErrorElement.',
      error,
    );
  }

  if (error instanceof NotFoundError) {
    captureException(error, {
      tags: { platform: 'not-found' },
    });
    return <NotFoundErrorPage error={error} />;
  }

  if (error instanceof PermissionDeniedError) {
    return <PermissionDeniedErrorPage error={error} />;
  }

  if (error instanceof InternalServerError) {
    captureException(error, {
      tags: { platform: 'internal-server-error' },
    });
    return <InternalServerErrorPage error={error} />;
  }

  if (error instanceof LoadingError) {
    return <LoadingErrorPage />;
  }

  if (isRouteErrorResponse(error)) {
    if (error.status === 401 || error.status === 403) {
      return (
        <PermissionDeniedErrorPage
          error={
            new PermissionDeniedError({
              reason: 'ACCESS_DENIED',
            })
          }
        />
      );
    }

    if (error.status === 404) {
      captureException(error, {
        tags: { platform: 'not-found' }, // this may be expected/inert, but worth knowing in case we have a broken link
      });
      return <NotFoundErrorPage />;
    }

    if (error.status === 503) {
      captureException(error, {
        tags: { platform: 'loading-failed' },
      });
      return <LoadingErrorPage />;
    }

    if (error.status >= 500) {
      captureException(error, {
        tags: { platform: 'internal-server-error' },
      });
      return (
        <InternalServerErrorPage
          error={
            new InternalServerError({
              actionProps: {
                label: 'Return to Homepage',
                to: generatePath('/'),
              },
            })
          }
        />
      );
    }
  }

  // TODO: S2CX-17110: uncomment to allow error to bubble up and remove the InternalServerErrorPage
  // throw error; // bubble-up the error
  return (
    <InternalServerErrorPage
      error={
        new InternalServerError({
          actionProps: {
            label: 'Return to Homepage',
            to: generatePath('/'),
          },
        })
      }
    />
  ); // fallback until we can bubble up the error
};
